import { useCallback, useEffect, useRef, useState } from "react";

import apiService from "../services/api";
import { normalizeGeoJsonResponse } from "../utils/clientActivityGeo";

const POLL_INTERVAL_MS = 60000;
const EMPTY_GEOJSON = {
  type: "FeatureCollection",
  features: [],
};

/**
 * Reseller-scoped live device map data from
 * GET /v1/dashboard/accounts/playlists/geo
 */
export function useResellerPlaylistGeo(enabled) {
  const [geoJson, setGeoJson] = useState(EMPTY_GEOJSON);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  const fetchGeoJson = useCallback(async () => {
    if (!enabled) {
      setGeoJson(EMPTY_GEOJSON);
      setLoading(false);

      return;
    }

    try {
      const response = await apiService.getResellerPlaylistGeo();
      const normalized = normalizeGeoJsonResponse(response);

      if (!isMountedRef.current) return;

      setGeoJson(normalized || EMPTY_GEOJSON);
      setError(null);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.warn("Failed to load reseller playlist map data", err);
      setError(err);
      setGeoJson(EMPTY_GEOJSON);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [enabled]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!enabled) {
      setLoading(false);

      return undefined;
    }

    setLoading(true);
    fetchGeoJson();

    const intervalId = setInterval(fetchGeoJson, POLL_INTERVAL_MS);

    return () => {
      isMountedRef.current = false;
      clearInterval(intervalId);
    };
  }, [enabled, fetchGeoJson]);

  return {
    geoJson,
    loading,
    error,
    refresh: fetchGeoJson,
  };
}
